import type { ErrorCode, ProblemParams } from '@volee/api-client'

import { EN_ERRORS, EN_NETWORK_ERROR, EN_UNKNOWN_ERROR } from './en/errors.ts'
import { IT_ERRORS, IT_NETWORK_ERROR, IT_UNKNOWN_ERROR } from './it/errors.ts'
import { locale } from './locale.ts'

// Picks the active language's error catalogue and interpolates `params` (frontend-v1 §4.5).
// `title`/`detail` are never read: English, for developers, logs only.
/** The catalogue of the active language. */
export const ERRORS: Record<ErrorCode, string> = locale === 'en' ? EN_ERRORS : IT_ERRORS

/** Shown when the backend sends a `code` this catalogue does not know. */
export const UNKNOWN_ERROR = locale === 'en' ? EN_UNKNOWN_ERROR : IT_UNKNOWN_ERROR

/** Shown when the network never answered: there is no `code` to translate. */
export const NETWORK_ERROR = locale === 'en' ? EN_NETWORK_ERROR : IT_NETWORK_ERROR

function isKnownErrorCode(code: string): code is ErrorCode {
  return Object.hasOwn(ERRORS, code)
}

/** Interpolates the problem document's `params`: `{club_id}` → value. A placeholder with no
 *  param stays put rather than becoming `undefined`, which would mutilate the sentence. */
function interpolateParams(text: string, params: ProblemParams | null): string {
  if (params === null) return text
  return text.replace(/\{(\w+)\}/g, (placeholder, key: string) => {
    const value = params[key]
    return value == null ? placeholder : String(value)
  })
}

/** The message for an API error. An unknown `code` — the backend can add one before this
 *  frontend does — falls back to the generic text. */
export function translateError(
  code: string | null | undefined,
  params: ProblemParams | null = null,
): string {
  if (code == null || !isKnownErrorCode(code)) return UNKNOWN_ERROR
  return interpolateParams(ERRORS[code], params)
}
